/**
 * Form Renderer Component.
 *
 * Renders a form template's fields as fillable inputs. Supports both
 * generic forms and inspection checklists with:
 * - Section grouping (Engine, Hydraulics, Electrical, etc.)
 * - Pass/Fail/Attention rating buttons with color-coded visual feedback
 * - Inspector notes field
 * - All standard field types (text, number, select, checkbox, date, textarea)
 */

import { useState, useMemo } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import type { FormFieldDefinition } from "@/types/form";
import Button from "@/components/ui/Button";

interface FormRendererProps {
  fields: FormFieldDefinition[];
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
  showInspectorNotes?: boolean;
  onInspectorNotesChange?: (notes: string) => void;
}

const inputClass =
  "mt-1 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)]";

const RATING_BUTTONS = [
  {
    value: "Pass",
    icon: CheckCircle2,
    color: "text-[var(--text-success)]",
    bg: "bg-[var(--fill-success)]",
    border: "border-[var(--text-success)]",
  },
  {
    value: "Attention",
    icon: AlertTriangle,
    color: "text-[var(--text-warning)]",
    bg: "bg-[var(--fill-warning)]",
    border: "border-[var(--text-warning)]",
  },
  {
    value: "Fail",
    icon: XCircle,
    color: "text-[var(--text-danger)]",
    bg: "bg-[var(--fill-danger)]",
    border: "border-[var(--text-danger)]",
  },
];

function FormRenderer({
  fields,
  onSubmit,
  isSubmitting,
  onCancel,
  showInspectorNotes,
  onInspectorNotesChange,
}: FormRendererProps) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [notes, setNotes] = useState("");

  const setValue = (name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(values);
  };

  // Group fields by section for inspection templates.
  const sections = useMemo(() => {
    const hasSections = fields.some((f) => f.section);
    if (!hasSections) return [{ name: null, fields }];

    const map = new Map<string, FormFieldDefinition[]>();
    const noSection: FormFieldDefinition[] = [];

    for (const field of fields) {
      if (field.section) {
        if (!map.has(field.section)) map.set(field.section, []);
        map.get(field.section)!.push(field);
      } else {
        noSection.push(field);
      }
    }

    const result: Array<{
      name: string | null;
      fields: FormFieldDefinition[];
    }> = [];
    for (const [name, sectionFields] of map) {
      result.push({ name, fields: sectionFields });
    }
    if (noSection.length) result.push({ name: null, fields: noSection });
    return result;
  }, [fields]);

  const renderField = (field: FormFieldDefinition) => (
    <div key={field.name}>
      <label className="block text-[13px] font-medium text-[var(--text-primary)]">
        {field.label}
        {field.required && (
          <span className="ml-1 text-[var(--text-danger)]">*</span>
        )}
      </label>

      {/* Pass / Fail / Attention buttons */}
      {(field.type === "pass_fail" || field.rating_type === "pass_fail") && (
        <div className="mt-1.5 flex gap-2">
          {RATING_BUTTONS.map((btn) => {
            const isSelected = values[field.name] === btn.value;
            return (
              <button
                key={btn.value}
                type="button"
                onClick={() => setValue(field.name, btn.value)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-[var(--radius-md)] border text-sm font-semibold transition-all ${
                  isSelected
                    ? `${btn.bg} ${btn.color} ${btn.border} shadow-sm`
                    : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:border-[var(--border-strong)]"
                }`}
              >
                <btn.icon className="h-4 w-4" />
                {btn.value}
              </button>
            );
          })}
        </div>
      )}

      {field.type === "text" && (
        <input
          type="text"
          required={field.required}
          placeholder={field.placeholder}
          value={(values[field.name] as string) || ""}
          onChange={(e) => setValue(field.name, e.target.value)}
          className={inputClass}
        />
      )}

      {field.type === "number" && (
        <input
          type="number"
          step="any"
          required={field.required}
          placeholder={field.placeholder}
          value={(values[field.name] as string) || ""}
          onChange={(e) =>
            setValue(field.name, parseFloat(e.target.value) || "")
          }
          className={inputClass}
        />
      )}

      {field.type === "textarea" && (
        <textarea
          required={field.required}
          placeholder={field.placeholder}
          value={(values[field.name] as string) || ""}
          onChange={(e) => setValue(field.name, e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
        />
      )}

      {field.type === "select" && field.options && (
        <select
          required={field.required}
          value={(values[field.name] as string) || ""}
          onChange={(e) => setValue(field.name, e.target.value)}
          className={inputClass}
        >
          <option value="">Select...</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {field.type === "checkbox" && (
        <div className="mt-2">
          <input
            type="checkbox"
            checked={!!values[field.name]}
            onChange={(e) => setValue(field.name, e.target.checked)}
            className="h-4 w-4 rounded border-[var(--border-default)] text-[var(--color-interactive-primary)]"
          />
        </div>
      )}

      {field.type === "date" && (
        <input
          type="date"
          required={field.required}
          value={(values[field.name] as string) || ""}
          onChange={(e) => setValue(field.name, e.target.value)}
          className={inputClass}
        />
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {sections.map((section, idx) => (
        <div key={section.name || idx}>
          {section.name && (
            <div className="flex items-center gap-2 mb-3 mt-2">
              <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                {section.name}
              </h4>
              <div className="flex-1 h-px bg-[var(--border-subtle)]" />
            </div>
          )}
          <div className="space-y-3">{section.fields.map(renderField)}</div>
        </div>
      ))}

      {/* Inspector notes */}
      {showInspectorNotes && (
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Inspector Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              onInspectorNotesChange?.(e.target.value);
            }}
            placeholder="Overall observations, recommendations, or concerns..."
            rows={3}
            className="mt-1 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" isLoading={isSubmitting}>
          Submit
        </Button>
      </div>
    </form>
  );
}

export default FormRenderer;
